import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RuntimeConfigService } from "../../../../services/runtime-config.service";
import { Observable, EMPTY } from "rxjs";
import { PaginationModel } from "../../shared/pagination-model";
import { map } from "rxjs";
import { UserService } from "../../../../services/user-service.service";
import { UserTypeEnum } from "../../../models/user-type.enum";
import { User } from "../../../models/user.model";
import { UserNotificationPrefs } from "../../../models/user-notification-prefs.model";
import { GridDataResult } from "@progress/kendo-angular-grid";
import { SortDescriptor, State } from "@progress/kendo-data-query";
import { CommunicationNotificationType } from "../../../models/communication-notification-type.enum";


@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private apiUrl: string;

  constructor(private http: HttpClient, private runtimeConfig: RuntimeConfigService, private userService: UserService) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/analytics';

    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/analytics';
    });
  }

  public getControllers(pageNumber: number, pageSize: number, startDate: Date, endDate: Date | null, sortField: string, sortDirection: 'asc' | 'desc', searchString?: string): Observable<PaginationModel<AnalyticsControllers>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('startDate', startDate.toISOString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (searchString) params = params.set('searchString', searchString);
    return this.http.get<PaginationModel<AnalyticsControllers>>(`${this.apiUrl}/GetControllers/`, { params: params }).pipe(
      map((response) => ({
        ...response,
        Results: response.Results.map((result) => ({
          ...result,
          SuccessData: result.ExecutionTimeGraph.map((g) => g.SuccessViews),
          FailureData: result.ExecutionTimeGraph.map((g) => g.FailureViews),
          Labels: result.ExecutionTimeGraph.map((g) => g.ExecutionRange),
          PieChartData: [
            { category: "Success", value: result.PercentSuccess, color: "#00cc00" },
            { category: "Client Error", value: result.PercentClientError, color: "#ff9900" },
            { category: "Server Error", value: result.PercentServerError, color: "#ff3300" }
          ].filter(item => item.value)
        }))
      }))
    );
  }

  public getControllerViews(pageNumber: number, pageSize: number, startDate: Date, endDate: Date | null, controllerName: string, sortField: string, sortDirection: 'asc' | 'desc', userId?: number, appSubPath?: string, filters?: ControllerViewFilters): Observable<PaginationModel<ControllerView>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('controllerName', controllerName)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (userId != null) params = params.set('userId', userId);
    if (appSubPath != null) params = params.set('appSubPath', appSubPath);
    params = this.applyViewFilters(params, filters);
    return this.http.get<PaginationModel<ControllerView>>(`${this.apiUrl}/GetControllerViews/`, { params });
  }

  public getControllerViewSummary(startDate: Date, endDate: Date | null, controllerName: string, userId?: number, appSubPath?: string, filters?: ControllerViewFilters): Observable<ControllerViewSummary> {
    let params = new HttpParams()
      .set('controllerName', controllerName)
      .set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (userId != null) params = params.set('userId', userId);
    if (appSubPath != null) params = params.set('appSubPath', appSubPath);
    params = this.applyViewFilters(params, filters);
    return this.http.get<ControllerViewSummary>(`${this.apiUrl}/GetControllerViewSummary/`, { params });
  }

  private applyViewFilters(params: HttpParams, filters?: ControllerViewFilters): HttpParams {
    if (filters?.statusCode != null) params = params.set('statusCode', filters.statusCode);
    if (filters?.minExecutionDuration != null) params = params.set('minExecutionDuration', filters.minExecutionDuration);
    if (filters?.subPathSearch) params = params.set('subPathSearch', filters.subPathSearch);
    if (filters?.userSearch) params = params.set('userSearch', filters.userSearch);
    return params;
  }

  public getControllerViewGroups(pageNumber: number, pageSize: number, startDate: Date, endDate: Date | null, controllerName: string, sortField: string, sortDirection: 'asc' | 'desc', searchString?: string, userSearch?: string): Observable<PaginationModel<ControllerViewGroup>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('controllerName', controllerName)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (searchString) params = params.set('searchString', searchString);
    if (userSearch) params = params.set('userSearch', userSearch);
    return this.http.get<PaginationModel<ControllerViewGroup>>(`${this.apiUrl}/GetControllerViewGroups/`, { params });
  }

  public getDashboard(startDate: Date, endDate: Date | null): Observable<AnalyticsDashboard> {
    let params = new HttpParams().set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    return this.http.get<AnalyticsDashboard>(`${this.apiUrl}/GetDashboard`, { params }).pipe(
      map((response) => ({
        ...response,
        LoginHistory: response.LoginHistory.map(g => {
          const dateStr = g.CreatedDateUTC as unknown as string;
          const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
          return {
            ...g,
            CreatedDateUTC: new Date(utcDateStr)
          };
        }),
        TableUsedData: response.Tables.map(t => t.UsedSpaceMB),
        TableUnusedData: response.Tables.map(t => t.UnusedSpaceMB)
      }))
    );
  }

  public refreshSchoolDashboard(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/RefreshSchoolDashboard`, {});
  }

  public getSchoolDashboard(schoolId: number | null): Observable<AnalyticsSchoolDashboardResult> {
    let params = new HttpParams();
    if (schoolId !== null) {
      params = params.set('schoolId', schoolId);
    }
    return this.http.get<AnalyticsSchoolDashboardResult>(`${this.apiUrl}/GetSchoolDashboard`, { params }).pipe(
      map(response => {
        const raw = response.LastUpdatedDateUTC as unknown as string | null;
        const lastUpdated = raw ? new Date(raw.endsWith('Z') ? raw : raw + 'Z') : null;
        return { ...response, LastUpdatedDateUTC: lastUpdated };
      })
    );
  }

  public refreshDeviceDashboard(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/RefreshDeviceDashboard`, {});
  }

  public getDeviceDashboard(schoolId: number | null, userTypeId: UserTypeEnum | null): Observable<AnalyticsDeviceResult> {
    let params = new HttpParams();
    if (schoolId !== null) {
      params = params.set('schoolId', schoolId);
    }
    if (userTypeId !== null) {
      params = params.set('userTypeId', userTypeId);
    }
    return this.http.get<AnalyticsDeviceResult>(`${this.apiUrl}/GetDeviceDashboard`, { params }).pipe(
      map(response => {
        const raw = response.LastUpdatedDateUTC as unknown as string | null;
        const lastUpdated = raw ? new Date(raw.endsWith('Z') ? raw : raw + 'Z') : null;
        return { ...response, LastUpdatedDateUTC: lastUpdated };
      })
    );
  }

  public getPosts(schoolId: number | null, startDate: Date, endDate: Date | null, maxAuthors: number): Observable<AnalyticsPostsResult> {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('maxAuthors', maxAuthors);
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    return this.http.get<AnalyticsPostsResult>(`${this.apiUrl}/GetPosts`, { params });
  }

  public getPostAuthorGrid(schoolId: number | null, startDate: Date, endDate: Date | null, pageNumber: number, pageSize: number): Observable<AnalyticsPostAuthorGridResult> {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    return this.http.get<AnalyticsPostAuthorGridResult>(`${this.apiUrl}/GetPostAuthorGrid`, { params }).pipe(
      map(result => ({
        ...result,
        Buckets: result.Buckets.map(b => ({ ...b, StartUtc: this.toUtcDate(b.StartUtc)!, EndUtc: this.toUtcDate(b.EndUtc)! }))
      }))
    );
  }

  public getChats(schoolId: number | null, startDate: Date, endDate: Date | null, maxAuthors: number): Observable<AnalyticsChatResult> {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('maxAuthors', maxAuthors);
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    return this.http.get<AnalyticsChatResult>(`${this.apiUrl}/GetChats`, { params });
  }

  public getUsers(schoolId: number | null, userTypeId: UserTypeEnum, pageNumber: number, pageSize: number, sortField: string, sortDirection: 'asc' | 'desc', searchString?: string | null): Observable<PaginationModel<User>> {
    let params = new HttpParams()
      .set('userTypeId', userTypeId)
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    if (searchString) params = params.set('searchString', searchString);
    return this.http.get<PaginationModel<User>>(`${this.apiUrl}/GetUsers`, { params });
  }

  public getUsersExport(schoolId: number | null, userTypeId: UserTypeEnum, sortField: string, sortDirection: 'asc' | 'desc', searchString?: string | null): Observable<User[]> {
    let params = new HttpParams()
      .set('userTypeId', userTypeId)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    if (searchString) params = params.set('searchString', searchString);
    return this.http.get<User[]>(`${this.apiUrl}/GetUsersExport`, { params });
  }

  public updateUserPreferences(userId: number, prefs: UserNotificationPrefs): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/UpdateUserPreferences`, {
      UserId: userId,
      NotificationPrefs: prefs
    });
  }

  public logPhoneClicked(phoneNumber: string, calledUserId?: number): Observable<void> {
    if (!this.userService.CurrentUserProfile) return EMPTY;
    return this.http.post<void>(`${this.apiUrl}/LogPhoneClicked`, { PhoneNumber: phoneNumber, CalledUserId: calledUserId });
  }

  public loadAutoNotices(state: State, sort: SortDescriptor | undefined, schoolId: number | null, startDate: Date, endDate: Date | null, searchString?: string | null): Observable<GridDataResult> {
    let params = new HttpParams()
      .set('skip', state.skip ?? 0)
      .set('take', state.take ?? 100)
      .set('sortField', sort?.field ?? 'CreatedDateUTC')
      .set('sortDirection', sort?.dir ?? 'desc')
      .set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    if (searchString) params = params.set('searchString', searchString);
    return this.http.get<PaginationModel<AnalyticsAutoNoticeAny>>(`${this.apiUrl}/GetAutoNotices/`, { params }).pipe(
      map(response => ({
        total: response.TotalCount,
        data: response.Results.map(n => ({
          ...n,
          CreatedDateUTC: this.toUtcDate(n.CreatedDateUTC)!,
          ProcessedDateUTC: this.toUtcDate(n.ProcessedDateUTC),
          ProcessedStartTime: this.toUtcDate(n.ProcessedStartTime),
          LatestDeliveryTimeUTC: this.toUtcDate(n.LatestDeliveryTimeUTC)
        }))
      }))
    );
  }

  public getAutoNoticeNotifications(autoNoticeType: AnalyticsAutoNoticeType, autoNoticeId: number, pageNumber: number, pageSize: number, sortField: string, sortDirection: 'asc' | 'desc', schoolId: number | null, startDate: Date, endDate: Date | null): Observable<PaginationModel<AnalyticsAutoNoticeNotification>> {
    let params = new HttpParams()
      .set('autoNoticeType', autoNoticeType)
      .set('autoNoticeId', autoNoticeId)
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    if (schoolId !== null) params = params.set('schoolId', schoolId);
    return this.http.get<PaginationModel<AnalyticsAutoNoticeNotification>>(`${this.apiUrl}/GetAutoNoticeNotifications/`, { params }).pipe(
      map(response => ({
        ...response,
        Results: response.Results.map(n => ({
          ...n,
          CreatedDateUTC: this.toUtcDate(n.CreatedDateUTC),
          ModifiedDateUTC: this.toUtcDate(n.ModifiedDateUTC),
          DeliveryTimeUTC: this.toUtcDate(n.DeliveryTimeUTC),
          EmailViewedDate: this.toUtcDate(n.EmailViewedDate)
        }))
      }))
    );
  }

  /** Server DateTimes serialize without a zone; treat them as UTC. */
  private toUtcDate(value: Date | string | null): Date | null {
    if (!value) return null;
    const str = value as unknown as string;
    return new Date(str.endsWith('Z') ? str : str + 'Z');
  }

  public getResponseTimeColor(responseTime: number): string {
    const minGreenMs = 200;   // ≤ this is fully green
    const maxRedMs = 1500;    // ≥ this is fully red
    const curve = 1.3;        // >1 = stays green longer, <1 = turns red faster
    const saturation = 70;
    const lightness = 45;

    const clamped = Math.min(
      Math.max(responseTime, minGreenMs),
      maxRedMs
    );

    // Normalize to 0–1
    const t = (clamped - minGreenMs) / (maxRedMs - minGreenMs);

    // Apply curve
    const curved = Math.pow(t, curve);

    // Interpolate hue: green (120) → red (0)
    const hue = 120 - 120 * curved;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  public formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) {
      return '';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    if (bytes === 0) {
      return '';
    }

    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    // No decimals for bytes or values >= 10, one decimal otherwise
    const formatted =
      i === 0
        ? size.toString()
        : size >= 10
          ? Math.round(size).toString()
          : size.toFixed(1);

    return `${formatted}${units[i]}`;
  }


}

export type AnalyticsDashboard = {
  LoginHistory: AnalyticsDashboardEntry[],
  LoginUsage: AnalyticsDashboardEntry,
  OnlineNow: number,
  OnlineLastDay: number,
  AppUsage: AnalyticsAppUsageResult | null,
  Tables: {
    TableName: string,
    SchemaName: string,
    Rows: number,
    TotalSpaceMB: number,
    UsedSpaceMB: number,
    UnusedSpaceMB: number
  }[]
  TableUsedData: number[],
  TableUnusedData: number[],
  TotalRows: number,
  TotalMemoryMB: number
}

export type AnalyticsAppUsageResult = {
  TotalRequests: number,
  Apps: AnalyticsAppUsageApp[],
  DataAvailable: boolean,
  /** Set when the range reached past Loki's lookback: usage covers only from here. */
  CoveredFrom: string | null
}

export type AnalyticsAppUsageApp = {
  AppId: number | null,
  AppName: string,
  AppPath: string,
  TotalRequests: number,
  PercentOfTotal: number,
  // Busiest first. Null for pie-only apps.
  Endpoints: AnalyticsAppUsageEndpoint[] | null,
  Others: AnalyticsAppUsageEndpoint | null
}

export type AnalyticsAppUsageEndpoint = {
  Endpoint: string,
  Count: number,
  PercentOfApp: number,
  OtherData: AnalyticsAppUsageEndpoint[] | null
}

export type AnalyticsDashboardEntry = {
  TotalOnline: number,
  StaffOnline: number,
  StudentsOnline: number,
  GuardiansOnline: number,
  CreatedDateUTC: Date
}

export type ControllerView = {
  Id: number,
  AppId: number,
  AppName: string,
  AppIconPath: string,
  UserId: number,
  UserName: string,
  ControllerPath: string,
  AppSubPath: string,
  CreatedBy: number,
  CreatedDateUTC: string,
  ExecutionDuration: number,
  StatusCode: number,
  RequestSize?: number,
  ResponseSize?: number,
  ClientVersion?: string
}

export type ControllerViewFilters = {
  statusCode?: number | null,
  minExecutionDuration?: number | null,
  subPathSearch?: string | null,
  userSearch?: string | null
}

export type ControllerViewSummary = {
  Count: number,
  MedianExecutionTime?: number,
  AverageExecutionTime?: number,
  MaxExecutionTime?: number
}

export type ControllerViewGroup = {
  UserId: number,
  UserName: string,
  AppId: number,
  AppName: string,
  AppIconPath: string,
  AppSubPath: string,
  Count: number,
  AverageExecutionTime?: number,
  P50ExecutionTime?: number,
  P95ExecutionTime?: number,
  MaxExecutionTime?: number,
  AverageResponseSize?: number,
  SuccessCount: number,
  FailureCount: number,
  LastSeenUTC: string
}

export type AnalyticsControllers = {
  ControllerPath: string,
  AverageExecutionTime?: number,
  P50ExecutionTime?: number,
  P95ExecutionTime?: number,
  P99ExecutionTime?: number,
  MaxExecutionTime?: number,
  SpikeCount?: number,
  SpikeUserCount?: number,
  SpikeSubPathCount?: number,
  SpikeMinuteCount?: number,
  AverageResponseSize?: number,
  TotalViews: number,
  // null when the server couldn't compute it (the rest of the row is still valid)
  UniqueViews: number | null,
  PercentSuccess: number,
  PercentClientError: number,
  PercentServerError: number,
  ExecutionTimeGraph: {
    ControllerPath: string,
    ExecutionRange: string,
    TotalViews: number,
    SuccessViews: number,
    FailureViews: number
  }[],
  SuccessData: number[],
  FailureData: number[],
  Labels: string[],
  PieChartData: { category: string, value: number, color: string }[]
}

export type AnalyticsSchoolDashboardResult = {
  LastUpdatedDateUTC: Date | null;
  TotalStudents: number;
  TotalGuardians: number;
  TotalStaff: number;
  TotalContactable: number;
  TotalNonContactable: number;
  LanguageData: AnalyticsSchoolDashboardLanguageData[];
  TimezoneData: AnalyticsSchoolDashboardTimezoneData[];
  EmailPostNotifications: number;
  EmailChatNotifications: number;
  TextPostNotifications: number;
  TextChatNotifications: number;
  PushPostNotifications: number;
  PushChatNotifications: number;
  MobileSummary: AnalyticsMobileSummary[];
}

export type AnalyticsDeviceResult = {
  LastUpdatedDateUTC: Date | null;
  PlatformData: AnalyticsDevicePlatformResult[];
  OsVersionData: AnalyticsDeviceOsVersionResult[];
}

export type AnalyticsDevicePlatformResult = {
  Platform: string;
  DeviceCount: number;
}

export type AnalyticsDeviceOsVersionResult = {
  Platform: string;
  OsVersion: string;
  DeviceCount: number;
}

export type AnalyticsMobileSummary = {
  UserType: 'Guardian' | 'Staff' | 'Student';
  NeverLoggedIn: number;
  LoggedInNonMobileOnly: number;
  LoggedInOnMobile: number;
}

export type AnalyticsSchoolDashboardLanguageData = {
  SnapshotDate: string;
  LanguagePreference: string;
  Count: number;
  OtherData?: AnalyticsSchoolDashboardLanguageData[];
}

export type AnalyticsSchoolDashboardTimezoneData = {
  SnapshotDate: string;
  Timezone: string;
  Count: number;
  OtherData?: AnalyticsSchoolDashboardTimezoneData[];
}

export type AnalyticsPostsResult = {
  TotalPosts: number,
  AveragePostsPerDay: number,
  TimeData: AnalyticsPostsTimeGraph[],
  TopAuthors: AnalyticsPostsTopAuthor[]
}

export type AnalyticsPostsTimeGraph = {
  PostDate: Date,
  NormalCount: number,
  TimeSensitiveCount: number,
  CriticalCount: number,
  QuickNoticeCount: number
}

export type AnalyticsPostsTopAuthor = {
  UserId: number,
  UserName: string,
  Count: number
}

export type AnalyticsPostAuthorGridResult = {
  Buckets: AnalyticsTimeBucket[],
  Rows: PaginationModel<AnalyticsPostAuthorRow>
}

export type AnalyticsTimeBucket = {
  Index: number,
  StartUtc: Date,
  EndUtc: Date,
  IsExtended: boolean
}

export type AnalyticsPostAuthorRow = {
  UserId: number,
  FirstName: string | null,
  LastName: string | null,
  UserName: string | null,
  TotalPosts: number,
  TotalViews: number,
  TotalLikes: number,
  Cells: AnalyticsPostAuthorCell[]
}

export type AnalyticsPostAuthorCell = {
  BucketIndex: number,
  PostCount: number,
  TotalViews: number,
  TotalLikes: number,
  MostPopularPostTitle: string | null
}

export type AnalyticsChatResult = {
  TotalMessages: number,
  AverageMessagesPerDay: number,
  TotalAttachments: number;
  GuardianToStaffResponseRate: number,
  TotalGuardianMessages: number,
  TimeData: AnalyticsChatTimeGraph[],
  TopAuthors: AnalyticsChatTopAuthor[]
}

export type AnalyticsChatTimeGraph = {
  ChatDate: Date,
  ChatCount: number,
  AttachmentCount: number,
  RatingCount: number
}

export type AnalyticsChatTopAuthor = {
  UserId: number,
  UserName: string,
  Count: number
}

/** (AutoNoticeType, Id) is the unique key; Ids overlap across types. */
export type AnalyticsAutoNotice = {
  Id: number,
  AutoNoticeType: AnalyticsAutoNoticeType,
  SubType: AnalyticsAutoNoticeSubType,
  Status: AnalyticsAutoNoticeStatus,
  CreatedDateUTC: Date,
  ProcessedDateUTC: Date | null,
  ProcessedStartTime: Date | null,
  SchoolId: number | null,
  SchoolName: string | null,
  SentUsers: number[],
  EmailSent: number,
  SmsSent: number,
  FailedCount: number,
  QueuedCount: number,
  LatestDeliveryTimeUTC: Date | null
}

export type AnalyticsAutoNoticeAttendance = AnalyticsAutoNotice & {
  AutoNoticeType: AnalyticsAutoNoticeType.Attendance,
  SubType: AnalyticsAutoNoticeSubType.PERIOD | AnalyticsAutoNoticeSubType.DAY,
  PeriodsMissed: string | null,
  DateToday: string | null,
  SchoolPhone: string | null,
  StudentUserId: number | null,
  StudentName: string | null
}

export type AnalyticsAutoNoticeBehavior = AnalyticsAutoNotice & {
  AutoNoticeType: AnalyticsAutoNoticeType.Behavior,
  SubType: AnalyticsAutoNoticeSubType.POSITIVE | AnalyticsAutoNoticeSubType.NEGATIVE,
  BehaviorDate: string | null,
  BehaviorEventId: number | null,
  BehaviorNotes: string | null,
  Observations: string | null,
  ActionsTaken: string | null,
  StudentNotes: string | null,
  StudentUserId: number | null,
  StudentName: string | null,
  TeacherName: string | null,
  ParentNames: string | null
}

export type AnalyticsAutoNoticeESSA = AnalyticsAutoNotice & {
  AutoNoticeType: AnalyticsAutoNoticeType.ESSA,
  SubType: AnalyticsAutoNoticeSubType.ESSA,
  AcademicYearNum: number,
  GuardianUserId: number | null,
  GuardianName: string | null,
  PrincipalStaffId: number,
  PrincipalName: string | null
}

export type AnalyticsAutoNoticeAny = AnalyticsAutoNoticeAttendance | AnalyticsAutoNoticeBehavior | AnalyticsAutoNoticeESSA;

export type AnalyticsAutoNoticeNotification = {
  Id: number,
  UserId: number,
  UserName: string | null,
  Type: CommunicationNotificationType,
  Status: AnalyticsNotificationStatus,
  CreatedDateUTC: Date | null,
  ModifiedDateUTC: Date | null,
  DeliveryTimeUTC: Date | null,
  EmailViewed: boolean,
  EmailViewedDate: Date | null,
  ErrorMessage: string | null,
  ExternalReferenceId: string | null,
  ServerName: string | null
}

/** Mirrors the server's CommunicationNotificationStatusEnum. */
export enum AnalyticsNotificationStatus {
  Queued = 0,
  Pending = 1,
  Sent = 2,
  Failed = 3,
  Skipped = 4,
  PendingRetry = 5,
  ProcessingRetry = 6,
  SuppressedMuted = 7
}

export enum AnalyticsAutoNoticeType {
  Attendance = 0,
  Behavior = 1,
  ESSA = 2
}

export enum AnalyticsAutoNoticeSubType {
  PERIOD = 0,
  DAY = 1,
  POSITIVE = 2,
  NEGATIVE = 3,
  /**Unused */
  ESSA = 4
}

export enum AnalyticsAutoNoticeStatus {
  Queued = 0,
  Pending = 1,
  NotificationRecordCreated = 2,
  NoRecipients = 3,
  Failed = 4,
  DoNotContactEnabled = 5
}


